// NOTE: Search for for a example of MenuGroupType lineItemsTypesForCarrierPayNew
import {
  For,
  JSX,
  Show,
  createSignal,
  createMemo,
  createEffect,
  onCleanup,
} from 'solid-js';
import { MenuDownIcon } from '@atgfr8/icons';
import { Portal } from 'solid-js/web';
import './Styles.css';

export type MenuItemType = {
  label: string | JSX.Element;
  value: string;
  disabled?: boolean;
};

export type MenuGroupType = {
  label: string;
  items: MenuItemType[];
};

export type SearchableInputSelectItems = MenuItemType[] | MenuGroupType[];

const isGrouped = (
  items: SearchableInputSelectItems,
): items is MenuGroupType[] => items.length > 0 && 'items' in items[0];

type SelectInputProps = {
  label?: string;
  menuItems: SearchableInputSelectItems;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  error?: string | null;
  style?: JSX.CSSProperties;
  required?: boolean;
  dataTestId: string;
  size?: 'sm' | 'md' | 'lg';
  class?: string;
};

const DROPDOWN_MAX_HEIGHT = 240;

const fieldClasses = (opts: {
  base: string;
  height: string;
  disabled?: boolean;
  hasError: boolean;
  hasValue: boolean;
  userClass?: string;
}) =>
  `select-input peer w-full py-2 border rounded-sm shadow-xs
   focus:outline-hidden disabled:border-gray-300
   ${opts.base}
   ${opts.height}
   ${opts.disabled ? 'select-input-disabled' : ''}
   ${
     opts.hasError && !opts.disabled
       ? 'border-error-red'
       : 'border-input-border hover:border-armstrong-primary focus:border-armstrong-primary'
   }
   ${opts.hasValue && !opts.disabled ? 'select-input-has-value' : ''}
   ${opts.userClass ?? ''}`;

const optionClasses = (opts: {
  disabled?: boolean;
  highlighted: boolean;
  selected: boolean;
}) =>
  `px-3 py-2 text-sm
   ${opts.disabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'}
   ${opts.highlighted && !opts.disabled ? 'bg-gray-100' : ''}
   ${opts.selected ? 'text-armstrong-primary font-medium' : 'text-gray-900'}`;

const headerClasses =
  'px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 select-none';

type NormalizedGroup = { label?: string; items: MenuItemType[] };

type RenderGroup = NormalizedGroup & { startIndex: number };

export const SelectInputSearchable = (props: SelectInputProps) => {
  const hasError = () => Boolean(props.error);
  const hasValue = () => props.value !== undefined && props.value !== '';

  const heightClass = () => {
    if (props.size === 'sm') return 'h-[32px]  text-xs';
    if (props.size === 'md') return 'h-[40px]';
    if (props.size === 'lg') return 'h-[48px]';
    return 'h-[40px]';
  };

  const normalizedGroups = createMemo<NormalizedGroup[]>(() => {
    const items = props.menuItems;
    if (isGrouped(items)) {
      return items.map((g) => ({
        label: g.label,
        items: (g.items as MenuItemType[] | undefined) ?? [],
      }));
    }
    return [{ label: undefined, items: items as MenuItemType[] }];
  });

  const allItems = createMemo(() => normalizedGroups().flatMap((g) => g.items));

  //NOTE: BELOW STATE ONLY WHEN PROP IS SET TO SEARCHABLE
  const [isOpen, setIsOpen] = createSignal(false);
  const [query, setQuery] = createSignal('');
  const [highlightedIndex, setHighlightedIndex] = createSignal(-1);

  const [pos, setPos] = createSignal({
    top: 0,
    left: 0,
    width: 0,
    openUp: false,
  });

  let inputRef: HTMLInputElement | undefined;
  let containerRef: HTMLDivElement | undefined;
  let listRef: HTMLUListElement | undefined;

  const itemLabelString = (item?: MenuItemType) =>
    item ? (typeof item.label === 'string' ? item.label : item.value) : '';

  const selectedItem = () =>
    allItems().find((item) => item.value === props.value);

  const selectedLabel = () => {
    const item = selectedItem();
    if (item) return itemLabelString(item);
    return props.value ?? '';
  };

  const matchesQuery = (item: MenuItemType, q: string) => {
    const labelMatch =
      typeof item.label === 'string' && item.label.toLowerCase().includes(q);
    return labelMatch || item.value.toLowerCase().includes(q);
  };

  const filteredGroups = createMemo<RenderGroup[]>(() => {
    const q = query().trim().toLowerCase();
    const showAll = !q || q === selectedLabel().toLowerCase();

    const result: RenderGroup[] = [];
    let offset = 0;
    for (const group of normalizedGroups()) {
      const items = showAll
        ? group.items
        : group.items.filter((item) => matchesQuery(item, q));
      if (items.length === 0) continue; // hide categories with no matches
      result.push({ label: group.label, items, startIndex: offset });
      offset += items.length;
    }
    return result;
  });

  const flatFilteredItems = createMemo(() =>
    filteredGroups().flatMap((g) => g.items),
  );

  createEffect(() => {
    if (!isOpen()) setQuery(selectedLabel());
  });

  const updatePosition = () => {
    if (!inputRef) return;
    const rect = inputRef.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
    setPos({
      top: openUp ? rect.top : rect.bottom,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  };

  createEffect(() => {
    if (!isOpen()) return;
    updatePosition();

    const onScroll = (e: Event) => {
      // scrolling the options list itself — keep open
      if (listRef && e.target instanceof Node && listRef.contains(e.target)) {
        return;
      }
      // user is actively typing — keep open, just realign the dropdown
      if (document.activeElement === inputRef) {
        updatePosition();
        return;
      }
      closeMenu();
    };

    window.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', updatePosition);
    onCleanup(() => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', updatePosition);
    });
  });

  const firstEnabledIndex = (items: MenuItemType[]) => {
    const i = items.findIndex((it) => !it.disabled);
    return i >= 0 ? i : 0;
  };

  const openMenu = () => {
    if (props.disabled) return;
    updatePosition();
    setIsOpen(true);
    const flat = flatFilteredItems();
    const selIdx = flat.findIndex((i) => i.value === props.value);
    setHighlightedIndex(selIdx >= 0 ? selIdx : firstEnabledIndex(flat));
  };

  const closeMenu = () => {
    setIsOpen(false);
    setQuery(selectedLabel());
    setHighlightedIndex(-1);
  };

  const selectItem = (item: MenuItemType) => {
    if (item.disabled) return;
    props.onChange?.(item.value);
    setQuery(itemLabelString(item));
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const scrollHighlightedIntoView = (idx: number) => {
    const node = listRef?.querySelector<HTMLElement>(`[data-index="${idx}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  };

  const moveHighlight = (dir: 1 | -1) => {
    const items = flatFilteredItems();
    if (items.length === 0) return;
    let idx = highlightedIndex();
    // Skip disabled options.
    for (let step = 0; step < items.length; step++) {
      idx = (idx + dir + items.length) % items.length;
      if (!items[idx].disabled) break;
    }
    setHighlightedIndex(idx);
    scrollHighlightedIntoView(idx);
  };

  const onInputKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        isOpen() ? moveHighlight(1) : openMenu();
        break;
      case 'ArrowUp':
        e.preventDefault();
        isOpen() ? moveHighlight(-1) : openMenu();
        break;
      case 'Enter': {
        if (!isOpen()) return;
        e.preventDefault();
        const item = flatFilteredItems()[highlightedIndex()];
        (item as undefined | MenuItemType) && selectItem(item);
        break;
      }
      case 'Escape':
        if (isOpen()) {
          e.preventDefault();
          closeMenu();
        }
        break;
      case 'Tab':
        if (isOpen()) closeMenu();
        break;
    }
  };

  createEffect(() => {
    if (!isOpen()) return;
    const handleOutsidePointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef &&
        !containerRef.contains(target) &&
        listRef &&
        !listRef.contains(target)
      ) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleOutsidePointer);
    onCleanup(() =>
      document.removeEventListener('mousedown', handleOutsidePointer),
    );
  });

  return (
    <div ref={containerRef} class="relative w-full min-w-0">
      <input type="hidden" name={props.name} value={props.value ?? ''} />

      <input
        ref={inputRef}
        type="text"
        style={props.style ?? undefined}
        data-testid={props.dataTestId}
        placeholder={props.placeholder}
        autocomplete="off"
        role="combobox"
        aria-expanded={isOpen()}
        aria-controls={`${props.dataTestId}-listbox`}
        aria-autocomplete="list"
        aria-invalid={hasError() || undefined}
        value={query()}
        disabled={props.disabled}
        onFocus={() => {
          openMenu();
          queueMicrotask(() => inputRef?.select());
        }}
        onInput={(e) => {
          setQuery(e.currentTarget.value);
          if (!isOpen()) setIsOpen(true);
          setHighlightedIndex(0);
        }}
        onKeyDown={onInputKeyDown}
        onBlur={() => props.onBlur?.()}
        class={fieldClasses({
          base: 'cursor-text pl-3 pr-8',
          height: heightClass(),
          disabled: props.disabled,
          hasError: hasError(),
          hasValue: hasValue(),
          userClass: props.class,
        })}
      />

      <span class="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-gray-500">
        <MenuDownIcon />
      </span>

      <Show when={isOpen() && !props.disabled}>
        <Portal>
          <ul
            ref={listRef}
            id={`${props.dataTestId}-listbox`}
            role="listbox"
            style={{
              position: 'fixed',
              top: `${pos().top}px`,
              left: `${pos().left}px`,
              width: `${pos().width}px`,
              transform: pos().openUp ? 'translateY(-100%)' : 'translateY(0)',
              'z-index': 1000,
            }}
            class="max-h-60 overflow-auto rounded-sm border border-input-border bg-white py-1 shadow-md min-w-[200px]"
          >
            <Show
              when={flatFilteredItems().length > 0}
              fallback={
                <li class="px-3 py-2 text-xs text-gray-500">No results</li>
              }
            >
              <For each={filteredGroups()}>
                {(group) => (
                  <>
                    <Show when={group.label}>
                      <li role="presentation" class={headerClasses}>
                        {group.label}
                      </li>
                    </Show>
                    <For each={group.items}>
                      {(item, index) => {
                        const flatIndex = () => group.startIndex + index();
                        return (
                          <li
                            role="option"
                            data-index={flatIndex()}
                            aria-selected={item.value === props.value}
                            aria-disabled={item.disabled || undefined}
                            // Prevent the input from blurring before the click registers.
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() =>
                              !item.disabled && setHighlightedIndex(flatIndex())
                            }
                            onClick={() => selectItem(item)}
                            class={optionClasses({
                              disabled: item.disabled,
                              highlighted: highlightedIndex() === flatIndex(),
                              selected: item.value === props.value,
                            })}
                          >
                            {item.label}
                          </li>
                        );
                      }}
                    </For>
                  </>
                )}
              </For>
            </Show>
          </ul>
        </Portal>
      </Show>

      <Show when={props.label}>
        <label
          class={`select-input-label absolute -top-2 left-2 px-1 text-xs font-medium pointer-events-none
            overflow-hidden whitespace-nowrap text-ellipsis max-w-[90%] ${
              hasError() ? 'text-error-red' : 'text-armstrong-primary'
            }`}
        >
          {props.label}
          {props.required && <span> *</span>}
        </label>
      </Show>

      {hasError() && (
        <div class="text-error-red text-xs mt-1 px-2">{props.error}</div>
      )}
    </div>
  );
};
